import React, { FC, RefObject } from 'react';
import classNames from 'classnames';

import { ClosePlayerButton } from '../../skin/close-player-button/component';
import { BaseControlName } from '../../../types/controls';
import { useSkinStage } from '../../../hooks/skin/use-skin-stage';
import { SubtitlesProvider } from '../../subtitles/subtitles-provider/components';
import { ContextMenuContainer } from '../../skin/context-menu/container';
import { UserActionIndicatorProvider } from '../../skin/user-action-indicator/user-action-indicator-provider/component';
import { UserActionIndicatorView } from '../../skin/user-action-indicator/user-action-indicator-view/component';
import { VodKeyboardController } from '../../../../vod-player/ui/VodKeyboardController';
import { VodLayout } from '../../../../vod-player/ui/VodLayout';
import { VodPlayButton } from '../../../../vod-player/ui/VodPlayButton';
import { GlobalKeyboardHandlerTogglersProvider } from '../../../contexts/global-keyboard-handler-togglers';
import { SubtitlesPortalRoot } from '../../subtitles/subtitles-portal-root/component';
import { useFilmPrerollDurationWatcher } from '../../../../film-player/hooks/use-film-preroll-duration-watcher';
import { FilmPlayerMetaContainer } from '../../../../film-player/ui/FilmPlayerMetaContainer';
import { FilmAgeRestriction } from '../../../../film-player/ui/FilmAgeRestriction';
import { FilmSkipControlsContainer } from '../../../../film-player/ui/FilmSkipControlsContainer';

import { useOnFilmEnd } from './hooks/use-on-film-end';
import styles from './FilmPlayerSkin.module.css';
import FilmPlayerControlPanel from './FilmPlayerControlPanel';
import { FilmPrerollViewContainer } from './FilmPrerollViewContainer';

interface FilmPlayerSkinProps {
    contentId: string;
    onClose?: () => void;
    className?: string;
    rootContainerRef: RefObject<HTMLDivElement> | null;
}

export const FilmPlayerSkin: FC<FilmPlayerSkinProps> = ({
    contentId,
    onClose,
    className,
    rootContainerRef,
}) => {
    useFilmPrerollDurationWatcher(contentId);

    const { checkIsStageCurrent, switchToPrerollStage, switchToDefaultStage } = useSkinStage();

    useOnFilmEnd(onClose);

    return (
        <SubtitlesProvider>
            <GlobalKeyboardHandlerTogglersProvider>
                <UserActionIndicatorProvider>
                    <VodKeyboardController
                        isMovieActionsBlocked={checkIsStageCurrent('graceCommunication')}
                        onClose={onClose}
                    />
                    <VodLayout
                        className={classNames(styles.layout, className)}
                        renderTopControls={
                            checkIsStageCurrent('default', 'graceCommunication', 'preroll')
                                ? () => (
                                      <ClosePlayerButton
                                          name={BaseControlName.CLOSE}
                                          onClick={onClose}
                                          className={styles.closeButton}
                                          contentId={contentId}
                                      />
                                  )
                                : undefined
                        }
                        renderBottomControls={
                            checkIsStageCurrent('default') || checkIsStageCurrent('preroll')
                                ? ({ setIsHidingControlsBlocked }) => (
                                      <div className={styles.controlPanelContainer}>
                                          <FilmPlayerControlPanel
                                              contentId={contentId}
                                              setIsHidingControlsBlocked={
                                                  setIsHidingControlsBlocked
                                              }
                                          />
                                      </div>
                                  )
                                : undefined
                        }
                        renderControls={({ isControlsVisible }) => (
                            <>
                                <UserActionIndicatorView className={styles.indicator} />
                                {checkIsStageCurrent('default') && (
                                    <>
                                        <SubtitlesPortalRoot
                                            isControlsVisible={isControlsVisible}
                                        />
                                        <VodPlayButton className={styles.playButton} />
                                        {!isControlsVisible && (
                                            <FilmAgeRestriction contentId={contentId} />
                                        )}
                                        <FilmSkipControlsContainer
                                            contentId={contentId}
                                            className={styles.skipButtonGroup}
                                        />
                                    </>
                                )}
                                {!checkIsStageCurrent('preroll') && (
                                    <FilmPlayerMetaContainer
                                        movieId={contentId}
                                        className={styles.meta}
                                        isControlsVisible={isControlsVisible}
                                    />
                                )}
                                <FilmPrerollViewContainer
                                    contentId={contentId}
                                    onStart={switchToPrerollStage}
                                    onClose={switchToDefaultStage}
                                    checkStage={checkIsStageCurrent}
                                    className={styles.prerollView}
                                />
                            </>
                        )}
                        renderPopups={() =>
                            rootContainerRef && (
                                <ContextMenuContainer rootContainerRef={rootContainerRef} />
                            )
                        }
                    />
                </UserActionIndicatorProvider>
            </GlobalKeyboardHandlerTogglersProvider>
        </SubtitlesProvider>
    );
};
